import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ListChecks, LogOut, Loader2, Hash, Scissors, Percent, AlertTriangle } from "lucide-react";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/useAuth";
import api from "@/lib/api";

function fmtDate(d) {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}
const fmtPct = (n) => `${Number(n).toFixed(1).replace(".", ",")}%`;

function MetaCell({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-3 border-r border-border last:border-r-0">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mono text-lg font-medium leading-none ${accent ? "text-[hsl(var(--success))]" : "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}

export default function ProductionQueue() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([api.get("/production/queue"), api.get("/production/summary")]);
      setQueue(q.data);
      setSummary(s.data);
    } catch (e) {
      toast.error("Erro ao carregar o painel de produção");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 bg-card">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/ordens")}
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
            title="Voltar às ordens"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 bg-foreground flex items-center justify-center">
            <ListChecks className="w-4 h-4 text-background" />
          </div>
          <div className="leading-none">
            <h1 className="font-head text-sm font-bold tracking-tight">PRODUÇÃO · {user?.company_name}</h1>
            <p className="text-[10px] text-muted-foreground mono mt-0.5">Fila de tecidos por cortar, por prazo de entrega</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground mono hidden sm:inline">{user?.name}</span>
          <button onClick={logout} title="Terminar sessão" className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="p-6 max-w-5xl mx-auto space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 border border-border bg-card">
                <MetaCell icon={Hash} label="Total peças" value={summary.total_pieces} />
                <MetaCell icon={Scissors} label="Cortadas" value={summary.cut_pieces} />
                <MetaCell icon={Percent} label="Progresso" value={fmtPct(summary.progress_pct)} accent={summary.progress_pct >= 100} />
                <MetaCell icon={AlertTriangle} label="Defeitos" value={summary.defects_count} />
              </div>
            )}

            {summary && (
              <div className="text-xs text-muted-foreground">
                {summary.fabrics_not_cut} por cortar · {summary.fabrics_cutting} em corte · {summary.fabrics_done} cortados
              </div>
            )}

            <div>
              <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-3">Fila de trabalho</h2>
              <div className="border border-border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Prazo</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Referência</TableHead>
                      <TableHead>Tecido</TableHead>
                      <TableHead className="text-right">Camadas</TableHead>
                      <TableHead className="text-right">Total peças</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {queue.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Nada por cortar de momento.
                        </TableCell>
                      </TableRow>
                    ) : (
                      queue.map((item) => (
                        <TableRow
                          key={item.fabric_id}
                          className="cursor-pointer"
                          onClick={() => navigate(`/ordens/${item.order_id}`)}
                          data-testid={`queue-row-${item.fabric_id}`}
                        >
                          <TableCell className="mono text-xs font-medium">{fmtDate(item.due_date)}</TableCell>
                          <TableCell>{item.client}</TableCell>
                          <TableCell className="mono text-xs">{item.reference}</TableCell>
                          <TableCell>{item.fabric_name}</TableCell>
                          <TableCell className="text-right mono">{item.layers}</TableCell>
                          <TableCell className="text-right mono">{item.total_pieces}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
